import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { useTheme } from '@/theme';
import { PauseableTimeout } from '@/utils/timeout';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { removeFirstNewsTickerItemFromQueue } from '@/state/features/feed';
import { NewsTickerDataResponse, NewsTickerItemData, NewsTickerItemDataFull } from '@/models/feed';
import { getNewsTickerData } from '@/arise/api';

import NewsTickerItem from './NewsTickerItem';
import * as styles from './styles.module.scss';

export type NewsTickerItemStatus = 'bottom' | 'middle' | 'top';

const REMOTE_DATA_POLL_FREQUENCY_MIN = 5; // Remote data poll frequency in MINUTES
const MIN_MESSAGE_VISIBILITY_MS = 2000; // Minimum amount of time a message must be visible for, in milliseconds
const MAX_MESSAGE_VISIBILITY_MS = 10000; // Maximum amount of time a message must be visible for, in milliseconds

const NewsTickerContainer = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const newsTickerQueue = useAppSelector((state) => state.feed.newsTickerQueue);
    const newsTickerTag = useAppSelector((state) => state.feed.newsTickerTag);
    const isNewsTickerVisible = useAppSelector((state) => state.app.isNewsTickerVisible);
    const customJsonData = theme.customJSON?.newsTicker;

    const [messageToShow, setMessageToShow] = useState<NewsTickerItemData>();

    const remoteData = useRef<NewsTickerDataResponse>();
    const highPriorityMessages = useRef<NewsTickerItemData[]>([]);
    const filteredGenericEvents = useRef([]);
    const genericEventsCurrentIndex = useRef(0);
    const currentNewsTickerTag = useRef('');
    const canShowMessage = useRef<boolean>(true); // e.g if MIN_MESSAGE_VISIBILITY_MS has elapsed

    const maxTimeout = useRef(new PauseableTimeout());
    const minTimeout = useRef(new PauseableTimeout());

    async function getRemoteData() {
        try {
            const response = await getNewsTickerData();
            const isValid =
                Array.isArray(response.genericEvents) &&
                response.genericEvents?.length > 0 &&
                response.hasOwnProperty('userSpecificEvents');
            if (!isValid) throw new Error('Data is invalid');
            remoteData.current = response;
            updateFilteredGenericEvents();
        } catch (error) {
            console.warn('Could not get news ticker data', error);
        }
    }

    function isFullNewsTickerItemData(item: NewsTickerItemData): item is NewsTickerItemDataFull {
        return 'type' in item;
    }

    function itemMatchesTag(item: NewsTickerItemData) {
        if (!currentNewsTickerTag?.current) return true;
        if (isFullNewsTickerItemData(item)) {
            return (
                !Array.isArray(item.tags) || item.tags.length === 0 || item.tags.includes(currentNewsTickerTag.current)
            );
        }
        return false;
    }

    function updateFilteredGenericEvents() {
        // Ensure only items which match current tag are shown
        const allGenericEvents = remoteData?.current?.genericEvents;
        if (allGenericEvents?.length) {
            filteredGenericEvents.current = allGenericEvents.filter(itemMatchesTag);
        } else {
            filteredGenericEvents.current = [];
        }
        genericEventsCurrentIndex.current = 0;

        // Show first low-priority message if nothing is showing and allowed
        if (!!customJsonData?.showImmediately && canShowMessage.current && !messageToShow) {
            showLowPriorityMessage();
        }
    }

    function addHighPriorityMessage(item: NewsTickerItemData) {
        if (isFullNewsTickerItemData(item)) {
            highPriorityMessages.current?.push(item);
        } else if ('id' in item) {
            // Find the message ID in the remote data and add it to the queue
            const message = remoteData.current?.userSpecificEvents?.[item.id];
            if (message) {
                highPriorityMessages.current?.push(message);
            }
        }
        showHighPriorityMessage();
    }

    useEffect(() => {
        // Whenever a message changes, time X seconds before showing a generic message
        if (messageToShow) {
            maxTimeout.current?.begin(() => {
                showLowPriorityMessage();
            }, MAX_MESSAGE_VISIBILITY_MS);
        }
        return () => {
            maxTimeout.current?.clear();
        };
    }, [messageToShow]);

    useEffect(() => {
        isNewsTickerVisible ? maxTimeout.current?.resume() : maxTimeout.current?.pause();
        isNewsTickerVisible ? minTimeout.current?.resume() : minTimeout.current?.pause();

        // If newsTicker becomes visible, but has no messages, start showing generic messages
        if (isNewsTickerVisible && canShowMessage.current && !messageToShow) {
            showLowPriorityMessage();
        }
    }, [isNewsTickerVisible]);

    function showHighPriorityMessage() {
        // Remove message from start of queue and show it
        if (canShowMessage.current && highPriorityMessages.current[0]) {
            const message = highPriorityMessages.current?.shift();
            showMessage(message);
        }
    }

    function showLowPriorityMessage() {
        // Get next message from generic array and show it
        if (filteredGenericEvents.current?.length) {
            // If index is too high, set back to 0
            if (genericEventsCurrentIndex.current >= filteredGenericEvents.current.length) {
                genericEventsCurrentIndex.current = 0;
            }
            const message = filteredGenericEvents.current[genericEventsCurrentIndex.current];
            showMessage(message);
            genericEventsCurrentIndex.current++;
        }
    }

    function showMessage(message: NewsTickerItemData) {
        if (message && canShowMessage.current) {
            setMessageToShow(message);
            canShowMessage.current = false;
            // Stop new messages from showing for X seconds
            minTimeout.current?.begin(() => {
                canShowMessage.current = true;
                showHighPriorityMessage();
            }, MIN_MESSAGE_VISIBILITY_MS);
        }
    }

    useEffect(() => {
        // When a news ticker message is added to state via the bridge...
        // Add it to the local array and remove it from the state.
        const newItem = newsTickerQueue[0];
        if (newItem) {
            addHighPriorityMessage(newItem);
            dispatch(removeFirstNewsTickerItemFromQueue());
        }
    }, [newsTickerQueue]);

    useEffect(() => {
        currentNewsTickerTag.current = newsTickerTag;
        updateFilteredGenericEvents();
    }, [newsTickerTag]);

    useEffect(() => {
        getRemoteData();
        // Get fresh remote data every n minutes
        const pollInterval = setInterval(getRemoteData, REMOTE_DATA_POLL_FREQUENCY_MIN * 60000);

        return () => {
            clearInterval(pollInterval);
        };
    }, []);
    return (
        <NewsTicker
            messageToShow={messageToShow}
            isVisible={isNewsTickerVisible}
            hasLowerTransparency={customJsonData?.lowerTransparency}
        />
    );
};

// Dumb component only controlled via its own props
const NewsTicker = ({
    messageToShow,
    isVisible,
    hasLowerTransparency,
}: {
    messageToShow: NewsTickerItemData;
    isVisible: boolean;
    hasLowerTransparency?: boolean;
}) => {
    const [itemAState, setItemAState] = useState<NewsTickerItemStatus>('bottom');
    const [itemBState, setItemBState] = useState<NewsTickerItemStatus>('top');
    const [itemCState, setItemCState] = useState<NewsTickerItemStatus>('middle');
    const [itemAMessage, setItemAMessage] = useState<NewsTickerItemData>();
    const [itemBMessage, setItemBMessage] = useState<NewsTickerItemData>();
    const [itemCMessage, setItemCMessage] = useState<NewsTickerItemData>();

    const numberOfMessagesSeen = useRef(0);

    useEffect(() => {
        if (messageToShow) {
            showMessage(messageToShow);
        }
    }, [messageToShow]);

    function getNextState(currentState: NewsTickerItemStatus) {
        if (currentState === 'top') return 'bottom';
        if (currentState === 'middle') return 'top';
        if (currentState === 'bottom') return 'middle';
    }

    function animateTicker() {
        setItemAState((prev) => getNextState(prev));
        setItemBState((prev) => getNextState(prev));
        setItemCState((prev) => getNextState(prev));
        numberOfMessagesSeen.current++;
    }

    function setBottomMessage(message: NewsTickerItemData) {
        const options = [setItemAMessage, setItemBMessage, setItemCMessage];
        const functionToUse = options[numberOfMessagesSeen.current % 3];
        functionToUse(message);
    }

    function showMessage(message: NewsTickerItemData) {
        setBottomMessage(message);
        animateTicker();
    }

    return (
        <div
            className={classNames(styles.NewsTicker, {
                [styles.hidden]: !isVisible,
                [styles.hasLowerTransparency]: hasLowerTransparency,
            })}
            aria-hidden={!isVisible}
            inert={!isVisible ? '' : undefined}
        >
            <div>
                <NewsTickerItem
                    status={itemAState}
                    message={itemAMessage}
                    hasLowerTransparency={hasLowerTransparency}
                />
                <NewsTickerItem
                    status={itemBState}
                    message={itemBMessage}
                    hasLowerTransparency={hasLowerTransparency}
                />
                <NewsTickerItem
                    status={itemCState}
                    message={itemCMessage}
                    hasLowerTransparency={hasLowerTransparency}
                />
            </div>
        </div>
    );
};

export default NewsTickerContainer;
